import { useState } from 'react';
import { ArrowRight, PenLine, Plus, Search, Signpost, Trash2, Wand2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useI18n } from '@/i18n';
import { api, toApiError } from '@/lib/api';
import { useInvalidate, useRedirects } from '@/lib/queries';
import { useToast } from '@/components/ui/Toast';
import { PageHeader } from '@/components/PageHeader';
import { Segmented } from '@/components/MediaPicker';
import { Button } from '@/components/ui/Button';
import { Field, Input, MonoInput } from '@/components/ui/Field';
import { Modal, Select, Switch, Tooltip } from '@/components/ui/primitives';
import { Chip } from '@/components/ui/Chip';
import { EmptyState, TableSkeleton } from '@/components/ui/Feedback';

/**
 * Most rows here are created automatically by a slug rename (§3.2), which is
 * why `automatic` / `manual` is a visible distinction: a marketer wondering
 * "where did this come from?" gets an answer without asking anyone.
 */
export function Redirects() {
  const { t, formatDate } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidate();

  const [query, setQuery] = useState('');
  const [source, setSource] = useState<'all' | 'system' | 'manual'>('all');
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useRedirects({ q: query || undefined, source });
  const rows = data?.items ?? [];

  return (
    <>
      <PageHeader
        title={t.redirects.title}
        subtitle={t.redirects.subtitle}
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
            {t.redirects.new}
          </Button>
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[14rem] max-w-sm flex-1">
            <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t.common.search}
              aria-label={t.common.search}
              className="pl-9"
            />
          </div>
          <Segmented
            value={source}
            onChange={setSource}
            options={[
              { value: 'all', label: t.common.all },
              { value: 'system', label: t.redirects.automatic },
              { value: 'manual', label: t.redirects.manual },
            ]}
          />
        </div>
      </PageHeader>

      <div className="mx-auto w-full max-w-content px-6 pb-12">
        <div className="card overflow-hidden">
          {isLoading ? (
            <TableSkeleton rows={5} cols={3} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Signpost className="h-6 w-6" />}
              title={t.redirects.emptyTitle}
              hint={t.redirects.emptyHint}
              action={
                <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
                  {t.redirects.emptyAction}
                </Button>
              }
            />
          ) : (
            <ul className="divide-y divide-line">
              {rows.map((redirect) => (
                <li
                  key={redirect.id}
                  className="row group flex flex-wrap items-center gap-3 px-4 py-3"
                >
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2 font-mono text-code">
                    <span className={cn('truncate', redirect.isActive ? 'text-strong' : 'text-faint line-through')}>
                      {redirect.from}
                    </span>
                    <ArrowRight aria-hidden className="h-3 w-3 shrink-0 text-faint" />
                    <span className="truncate text-muted">{redirect.to}</span>
                  </span>

                  <Chip tone={redirect.statusCode === 301 ? 'neutral' : 'amber'} mono>
                    {redirect.statusCode}
                  </Chip>

                  {/* Most rows here are created by a slug rename. Saying which
                      is which answers "where did this come from?" without a
                      trip to the audit log. */}
                  <Chip
                    tone="quiet"
                    icon={
                      redirect.createdBy === 'system' ? (
                        <Wand2 aria-hidden className="h-3 w-3" strokeWidth={1.85} />
                      ) : (
                        <PenLine aria-hidden className="h-3 w-3" strokeWidth={1.85} />
                      )
                    }
                    className="hidden sm:inline-flex"
                  >
                    {redirect.createdBy === 'system' ? t.redirects.automatic : t.redirects.manual}
                  </Chip>

                  <span className="hidden w-24 shrink-0 text-right text-ui text-faint lg:block">
                    {formatDate(redirect.createdAt)}
                  </span>

                  <span className="flex shrink-0 items-center gap-2">
                    {/*
                      The span is load-bearing. Radix's Tooltip.Trigger writes
                      its own `data-state` (open/closed) onto whatever element
                      it wraps, which silently clobbers the Switch's
                      checked/unchecked `data-state` — every switch then renders
                      as "off" regardless of its value. Wrapping keeps the two
                      state machines on separate elements.
                    */}
                    <Tooltip label={redirect.isActive ? t.redirects.turnOff : t.redirects.turnOn}>
                      <span className="inline-flex">
                        <Switch
                          checked={redirect.isActive}
                          onCheckedChange={async (checked) => {
                            await api.patch(`/redirects/${redirect.id}`, { isActive: checked });
                            void invalidate(['redirects']);
                          }}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip label={t.common.delete}>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={t.common.delete}
                        className="text-faint hover:text-accent-600 dark:hover:text-accent-400"
                        onClick={async () => {
                          await api.delete(`/redirects/${redirect.id}`);
                          void invalidate(['redirects']);
                          toast({ tone: 'info', message: t.common.deleted });
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.85} />
                      </Button>
                    </Tooltip>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {creating && (
        <RedirectDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            void invalidate(['redirects']);
            setCreating(false);
            toast({ tone: 'success', message: t.common.saved });
          }}
        />
      )}
    </>
  );
}

function RedirectDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [code, setCode] = useState<'301' | '302' | '308'>('301');
  const [busy, setBusy] = useState(false);

  const selfRedirect = from.length > 0 && from === to;

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={t.redirects.new}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!from || !to || selfRedirect}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post('/redirects', { from, to, statusCode: Number(code) });
                onSaved();
              } catch (error) {
                toast({ tone: 'error', message: toApiError(error).message });
              } finally {
                setBusy(false);
              }
            }}
          >
            {t.common.save}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t.redirects.from} hint={t.redirects.fromHint} error={selfRedirect ? t.redirects.selfRedirect : undefined}>
          {({ id, describedBy, invalid }) => (
            <MonoInput
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              autoFocus
              value={from}
              placeholder="/uz/news/eski-nom"
              onChange={(event) => setFrom(event.target.value)}
            />
          )}
        </Field>
        <Field label={t.redirects.to}>
          {({ id }) => (
            <MonoInput
              id={id}
              value={to}
              placeholder="/uz/news/yangi-nom"
              onChange={(event) => setTo(event.target.value)}
            />
          )}
        </Field>
        <Field label={t.redirects.code}>
          {({ id }) => (
            <Select
              id={id}
              value={code}
              onValueChange={setCode}
              options={[
                { value: '301', label: t.redirects.code301 },
                { value: '302', label: t.redirects.code302 },
                { value: '308', label: t.redirects.code308 },
              ]}
            />
          )}
        </Field>
      </div>
    </Modal>
  );
}
